import { buildS3UrlFromKey } from '../../utils/file-url';
import { get } from '../../utils/fetchAPI';

/**
 * Doctor profile file slots: `details.files.<name>` is `{ file, bucketName, key, visibility }`
 * (legacy: plain ObjectId string on the slot).
 */

export function snapshotFromUploadApiFile(fileDoc) {
    if (!fileDoc) {
        return { file: null, bucketName: '', key: '', visibility: '' };
    }
    const id = fileDoc._id ?? fileDoc.id;
    return {
        file: id != null ? String(id) : null,
        bucketName: fileDoc.s3Bucket != null ? String(fileDoc.s3Bucket) : '',
        key: fileDoc.s3Key != null ? String(fileDoc.s3Key) : '',
        visibility: fileDoc.visibility != null ? String(fileDoc.visibility) : '',
    };
}

/** Resolve stored file id for a slot (supports legacy plain id / populated doc). */
export function detailsFileSlotId(files, field) {
    const slot = files?.[field];
    if (slot == null || slot === '') return null;
    if (typeof slot === 'string') return slot;
    if (typeof slot === 'object') {
        if (slot.file != null) return String(slot.file);
        if (slot._id != null) return String(slot._id);
    }
    return null;
}

export function hasDetailsFileSlot(files, field) {
    return detailsFileSlotId(files, field) != null;
}

/**
 * Build S3 URL from stored details.files slot.
 *
 * @param slotObject details.files.<field> value ({ file, bucketName, key, visibility }).
 * @param options
 * @param options.concatPublic if true, always use REACT_APP_PUBLIC_S3_URL (ignore visibility).
 * @param options.concatPrivate if true, always use REACT_APP_PRIVATE_S3_URL (ignore visibility).
 *
 * Defaults are false. If neither is true, falls back to visibility-based base URL.
 */
export function detailsFileUrl(slotObject, { concatPublic = false, concatPrivate = false } = {}) {
    if (!slotObject || typeof slotObject !== 'object') return '';
    return buildS3UrlFromKey(slotObject.key, slotObject.visibility, {
        concatPublic,
        concatPrivate,
    });
}

/**
 * Read/display URL for details file slot.
 * Strict: S3 URL from env base + key only.
 */
export async function detailsFileDisplayUrl(slotObject, { concatPublic = false, concatPrivate = false } = {}) {
    if (!slotObject || typeof slotObject !== 'object') return '';

    // If concat is forced, skip visibility checks and use direct S3 URL.
    if (concatPublic || concatPrivate) {
        return detailsFileUrl(slotObject, { concatPublic, concatPrivate });
    }

    const visibility = String(slotObject.visibility || '').toLowerCase();
    if (visibility === 'private') {
        const fileId = slotObject.file != null ? String(slotObject.file) : '';
        if (!fileId) return '';
        try {
            const res = await get(`upload/files/${fileId}/signed-url`);
            return res?.data?.url || '';
        } catch (error) {
            return '';
        }
    }

    return detailsFileUrl(slotObject, { concatPublic, concatPrivate });
}
